import { toIsoDate } from "@common/helpers/date";
import type { ClientsCloseOverviewFilters } from "@common/schemas/clientsCloseOverview.schema";
import type { FiltersTypes } from "@common/types/filters/general";
import { isActive } from "@components/pageHeader/filters/lib";

interface BuildWorkQueryFiltersParams {
    search: string;
    /** The view's filters, with any Work end preset already resolved to dates. */
    tabFilters: FiltersTypes;
}

const toDateOnly = (date: Date | undefined) =>
    date ? toIsoDate(date) : undefined;

const nonEmpty = <TValue>(values: TValue[] | undefined) =>
    values && values.length > 0 ? values : undefined;

/** Clients per page of the Work overview: the server pages clients, not work. */
export const WORK_PAGE_SIZE = 20;

/** The filters the Work page sends with its overview, details and summary requests. */
export function buildWorkQueryFilters({
    search,
    tabFilters,
}: BuildWorkQueryFiltersParams): ClientsCloseOverviewFilters {
    const engagementTeamRoles = nonEmpty(tabFilters.engagementTeamRoles);
    const engagementTeamMemberIds = nonEmpty(
        tabFilters.engagementTeamMemberIds,
    );
    return {
        search: search || undefined,
        closeDateStart: toDateOnly(tabFilters.closeDateStart),
        closeDateEnd: toDateOnly(tabFilters.closeDateEnd),
        workStartDateStart: toDateOnly(tabFilters.workStartDateStart),
        workStartDateEnd: toDateOnly(tabFilters.workStartDateEnd),
        workDeliveryDateStart: toDateOnly(tabFilters.workDeliveryDateStart),
        workDeliveryDateEnd: toDateOnly(tabFilters.workDeliveryDateEnd),
        hasWorkDeliveryDate:
            tabFilters.workDeliveryDatePreset === "no_due_date"
                ? false
                : undefined,
        ...(isActive.daysInStage(tabFilters)
            ? {
                  daysInStageOperator: tabFilters.daysInStageOperator,
                  daysInStageMin: tabFilters.daysInStageMin,
                  daysInStageMax: tabFilters.daysInStageMax,
              }
            : {}),
        closeStageIds: nonEmpty(tabFilters.closeStageIds),
        closeStatuses: nonEmpty(tabFilters.closeStatuses),
        clientIds: nonEmpty(tabFilters.workClientIds),
        serviceIds: nonEmpty(tabFilters.orgServiceIds),
        noTier: tabFilters.orgServiceNoTier || undefined,
        closeTemplateIds: nonEmpty(tabFilters.closeTemplateIds),
        engagementTeamRoles,
        engagementTeamRoleOperator: engagementTeamRoles
            ? tabFilters.engagementTeamRoleOperator
            : undefined,
        engagementTeamMemberIds,
        engagementTeamMemberOperator: engagementTeamMemberIds
            ? tabFilters.engagementTeamMemberOperator
            : undefined,
        applicationTypes: nonEmpty(tabFilters.applicationTypes),
        progressStatuses: nonEmpty(tabFilters.progressStatuses),
        healthStatuses: nonEmpty(tabFilters.healthStatuses),
        hasUnread: tabFilters.hasUnread,
    };
}
